import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAdmin } from './hooks/useAdmin';
import {
  requestNotificationPermission,
  disableNotifications,
  getLastNotificationError,
  hasNotificationPermission,
  getNotificationPrefs,
  saveNotificationPrefs,
  DEFAULT_NOTIFICATION_TYPES
} from './utils/notificationService';
import { blockedNotificationHelp } from './utils/installPlatform';
import './NotificationSettings.css';

const TYPE_OPTIONS = [
  { key: 'followedResults', icon: '🏅', title: 'Results from dancers & studios I follow', note: 'Push + email' },
  { key: 'rankChanges', icon: '📈', title: 'Ranking changes of dancers & studios I follow', note: 'Push only' },
  { key: 'newCompetitions', icon: '📅', title: 'New competitions announced', note: 'Push + email' },
  { key: 'predictions', icon: '🔮', title: 'Predict Winner opens / my prediction results', note: 'Push + email' }
];

export default function NotificationSettings() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAdmin();
  const [types, setTypes] = useState(DEFAULT_NOTIFICATION_TYPES);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { kind: 'ok'|'error', text }
  const [pushOn, setPushOn] = useState(hasNotificationPermission());
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  const pushSupported = typeof window !== 'undefined' && 'Notification' in window;
  const blocked = pushSupported && Notification.permission === 'denied';

  useEffect(() => {
    if (!user) return;
    getNotificationPrefs(user)
      .then((p) => {
        setTypes(p.types);
        setEmailEnabled(p.emailEnabled);
      })
      .catch((e) => console.error('Could not load notification preferences:', e))
      .finally(() => setLoaded(true));
  }, [user]);

  const flash = (kind, text) => {
    setStatus({ kind, text });
    setTimeout(() => setStatus(null), 4000);
  };

  const persist = async (nextTypes, nextEmail) => {
    setSaving(true);
    try {
      await saveNotificationPrefs(user, { types: nextTypes, emailEnabled: nextEmail });
      flash('ok', 'Preferences saved');
    } catch (e) {
      console.error(e);
      flash('error', 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleType = (key) => {
    const next = { ...types, [key]: !types[key] };
    setTypes(next);
    persist(next, emailEnabled);
  };

  const toggleEmail = () => {
    const next = !emailEnabled;
    setEmailEnabled(next);
    persist(types, next);
  };

  const enablePush = async () => {
    setPushBusy(true);
    const token = await requestNotificationPermission(user, isAdmin);
    setPushOn(!!token);
    if (token) {
      await persist(types, emailEnabled); // make sure the prefs doc exists
      flash('ok', 'Push notifications are on for this device');
    } else {
      flash('error', getLastNotificationError() || 'Could not enable notifications. Check your browser permission.');
    }
    setPushBusy(false);
  };

  // Sends a real push to this account's devices through the same path the app's alerts use
  const sendTest = async () => {
    setTestBusy(true);
    try {
      const res = await httpsCallable(getFunctions(), 'sendTestNotification')();
      const { devices, push } = res.data || {};
      if (!devices) flash('error', 'No device is registered for your account yet. Turn push on for this device first.');
      else if (!push) flash('error', `Found ${devices} device${devices === 1 ? '' : 's'} but the message could not be delivered. Turn push off and on again to re-register.`);
      else flash('ok', `Test sent to ${push} of ${devices} device${devices === 1 ? '' : 's'}. It should appear in a moment.`);
    } catch (e) {
      console.error(e);
      flash('error', e.code === 'functions/not-found'
        ? 'The test function is not deployed yet — deploy the latest Cloud Functions.'
        : 'Could not send the test notification. Please try again.');
    } finally {
      setTestBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    const ok = await disableNotifications(user);
    if (ok) setPushOn(false);
    flash(ok ? 'ok' : 'error', ok ? 'Push notifications are off for this device' : 'Could not turn off. Please try again.');
    setPushBusy(false);
  };

  if (authLoading) return <div className="notif-page"><p>Loading…</p></div>;

  if (!user) {
    return (
      <div className="notif-page">
        <h2>🔔 Notifications</h2>
        <div className="notif-card">
          <p>Sign in to get alerts when dancers you follow place at a competition, rankings move, and new competitions or predictions open.</p>
          <button className="notif-primary" onClick={() => navigate('/login')}>Login / Sign Up</button>
        </div>
      </div>
    );
  }

  return (
    <div className="notif-page">
      <h2>🔔 Notifications</h2>
      <p className="notif-sub">
        Choose what you want to hear about. Follow dancers and studios from their profiles —
        updates show up in <Link to="/my-feed">My Feed</Link>.
      </p>

      {status && <div className={`notif-flash ${status.kind}`}>{status.text}</div>}

      <section className="notif-card">
        <h3>📱 Push notifications <span className="notif-pill">{pushOn ? 'On' : 'Off'}</span></h3>
        {!pushSupported && <p className="notif-warn">This browser doesn&apos;t support notifications.</p>}
        {blocked && (
          <p className="notif-warn">
            Notifications are blocked for this site. {blockedNotificationHelp()} Then come back and turn them on.
          </p>
        )}
        {pushSupported && !blocked && (
          pushOn ? (
            <button className="notif-secondary" onClick={disablePush} disabled={pushBusy}>
              {pushBusy ? 'Working…' : 'Turn off on this device'}
            </button>
          ) : (
            <button className="notif-primary" onClick={enablePush} disabled={pushBusy}>
              {pushBusy ? 'Working…' : 'Turn on for this device'}
            </button>
          )
        )}
        {pushOn && (
          <button className="notif-secondary" style={{ marginLeft: '0.6rem' }} onClick={sendTest} disabled={testBusy}>
            {testBusy ? 'Sending…' : 'Send a test notification'}
          </button>
        )}
        <p className="notif-hint">
          iPhone/iPad: add Greek Ranking to your Home Screen first (Share → Add to Home Screen), then enable alerts.
          {' '}<Link to="/install">Install guide for all devices</Link>
        </p>
      </section>

      <section className="notif-card">
        <h3>✉️ Email</h3>
        <label className="notif-row">
          <input type="checkbox" checked={emailEnabled} onChange={toggleEmail} disabled={!loaded || saving} />
          <span>
            <strong>Also email me</strong> at {user.email}
            <small>Results, new competitions and prediction results. You can unsubscribe any time here.</small>
          </span>
        </label>
      </section>

      <section className="notif-card">
        <h3>What should we notify you about?</h3>
        {TYPE_OPTIONS.map((opt) => (
          <label className="notif-row" key={opt.key}>
            <input
              type="checkbox"
              checked={types[opt.key] !== false}
              onChange={() => toggleType(opt.key)}
              disabled={!loaded || saving}
            />
            <span>
              <strong>{opt.icon} {opt.title}</strong>
              <small>{opt.note}</small>
            </span>
          </label>
        ))}
      </section>
    </div>
  );
}
